import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  IconButton,
  InputAdornment,
  FormHelperText,
  Typography,
  Divider,
  Link,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login, sendOtp } from '../../store/authSlice';
import SocialIcons from '../../components/SocialIcons/SocialIcons';
import MfaChallengeForm from './MfaChallengeForm';

interface HeroLoginFormProps {
  setType: (type: string) => void;
  onOpenModal?: () => void;
}

const HeroLoginForm: FC<HeroLoginFormProps> = ({ setType, onOpenModal }) => {
  const dispatch = useAppDispatch();
  const { mfaChallenge } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const [isOtp, setIsOtp] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  const handleClickShowPassword = () => setShowPassword((s) => !s);
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const formatEmail = (email: string) => (emailRegex.test(email) ? email : '+91' + email);
  const resend = (email: string) => {
    dispatch(sendOtp({ email: formatEmail(email) } as any));
    setSeconds(60);
    setOtpSent(true);
  };

  useEffect(() => {
    const t = seconds > 0 ? setTimeout(() => setSeconds(seconds - 1), 1000) : undefined;
    return () => clearTimeout(t);
  }, [seconds]);

  // Password accepted but MFA still owed — swap the form for the code screen.
  if (mfaChallenge) {
    return <MfaChallengeForm challenge={mfaChallenge} />;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111' }}>Login</h3>
        {/* <Typography
          onClick={() => { setType('1'); onOpenModal?.(); }}
          variant="body2"
          sx={{ cursor: 'pointer', color: '#059669', fontWeight: 500 }}
        >
          Create account
        </Typography> */}
      </div>

      <Formik
        initialValues={{ email: '', password: '', otp: '', agreeTerms: false, submit: null as any }}
        validationSchema={Yup.object().shape({
          email: Yup.string().required('Email or phone required'),
          password: isOtp ? Yup.string() : Yup.string().required('Password required').min(4, 'Min 4 chars'),
          otp: isOtp ? Yup.string().required('OTP required') : Yup.string(),
          agreeTerms: Yup.bool().oneOf([true], 'You must accept terms'),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setStatus({ success: false });
            setSubmitting(true);
            const email = formatEmail(values.email);
            const loginResponse: any = await dispatch(login((isOtp ? { email, password: '', otp: values.otp } : { email, password: values.password, otp: '' }) as any));
            // MFA still owed: the challenge screen takes over, there is no session yet.
            if (loginResponse.payload?.mfaRequired) {
              setSubmitting(false);
            } else if (loginResponse.type === 'auth/login/fulfilled') {
              navigate('/home', { replace: true });
            } else {
              setSubmitting(false);
              if (loginResponse.payload && 'message' in loginResponse.payload) {
                setErrors({ submit: loginResponse.payload.message });
              } else {
                setErrors({ submit: 'Login failed' });
              }
            }
          } catch (err: any) {
            setStatus({ success: false });
            setErrors({ submit: err?.message || 'Login failed' });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, values, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setFieldTouched }) => (
          <form noValidate onSubmit={handleSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>Email address or mobile</label>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter email / phone"
                name="email"
                value={values.email}
                onChange={(e) => { handleChange(e); setOtpSent(false); setSeconds(0); setFieldValue('otp', ''); }}
                onBlur={handleBlur}
                error={Boolean(touched.email && errors.email)}
                sx={textFieldSx}
              />
              {touched.email && errors.email && (
                <FormHelperText error>{errors.email as string}</FormHelperText>
              )}
            </div>

            {isOtp ? otpSent && (
            <div style={{ marginBottom: 6 }}>
              <label style={labelStyle}>Enter OTP</label>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter OTP"
                name="otp"
                value={values.otp}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.otp && errors.otp)}
                sx={textFieldSx}
              />
              {touched.otp && errors.otp && (
                <FormHelperText error>{errors.otp as string}</FormHelperText>
              )}
              {seconds > 0 ? (
                <Typography variant="caption" sx={{ color: '#888' }}>Resend OTP in {seconds}s</Typography>
              ) : (
                <Typography variant="caption" onClick={() => resend(values.email)} sx={{ cursor: 'pointer', color: '#059669', fontWeight: 600 }}>Resend OTP</Typography>
              )}
            </div>
            ) : (
            <div style={{ marginBottom: 6 }}>
              <label style={labelStyle}>Password</label>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.password && errors.password)}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                        {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {touched.password && errors.password && (
                <FormHelperText error>{errors.password as string}</FormHelperText>
              )}
            </div>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography
                onClick={() => {
                  setIsOtp(!isOtp);
                  setOtpSent(false);
                  setSeconds(0);
                }}
                variant="body2"
                sx={{ cursor: 'pointer', color: '#059669', fontWeight: 600 }}
              >
                {isOtp ? 'Login with Password' : 'Login with OTP'}
              </Typography>
              <Typography
                onClick={() => setType('2')}
                variant="body2"
                sx={{ cursor: 'pointer', color: '#111', fontWeight: 600 }}
              >
                Forgot password?
              </Typography>
            </Stack>

            <FormControlLabel
              sx={{ mt: 0, mb: 1, alignItems: 'flex-start', ml: 0, mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  name="agreeTerms"
                  checked={values.agreeTerms}
                  onChange={(e) => setFieldValue('agreeTerms', e.target.checked)}
                  sx={{ p: 0, mr: 0.75, mt: '2px' }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6, fontSize: 13 }}>
                  By Signing up, you agree to our{' '}
                  <Link component={RouterLink} to="/terms-of-use" sx={{ color: '#059669', textDecoration: 'underline' }}>
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link component={RouterLink} to="/privacy-policy" sx={{ color: '#059669', textDecoration: 'underline' }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {touched.agreeTerms && errors.agreeTerms && (
              <FormHelperText error sx={{ mb: 1 }}>{errors.agreeTerms as string}</FormHelperText>
            )}

            {errors.submit && (
              <FormHelperText error sx={{ mb: 1 }}>{errors.submit as string}</FormHelperText>
            )}

            <button
              type={isOtp && !otpSent ? 'button' : 'submit'}
              onClick={isOtp && !otpSent ? (e) => (e.preventDefault(), values.email ? resend(values.email) : setFieldTouched('email', true)) : undefined}
              disabled={isSubmitting}
              style={{
                width: '100%',
                background: '#059669',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isOtp && !otpSent ? 'Send OTP' : isSubmitting ? 'Logging in...' : 'Login'}
            </button>

            {/* Social sign-in hidden for now.
            <div style={{ margin: '12px 0 10px' }}>
              <Divider>
                <Typography variant="caption" sx={{ color: '#888' }}>Login with</Typography>
              </Divider>
            </div>

            <SocialIcons />
            */}
          </form>
        )}
      </Formik>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#333',
  marginBottom: 6,
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#059669' },
    '&.Mui-focused fieldset': { borderColor: '#059669' },
  },
};

export default HeroLoginForm;
